import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Tree, 
  Card, 
  Button, 
  Form, 
  Input, 
  Select, 
  Modal, 
  message, 
  Space, 
  Typography, 
  Tag,
  Popconfirm,
  Switch,
  Tooltip,
  Divider,
  Alert,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  FileOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { taxCategoriesService } from '../../services/taxCategoriesService';
import './TaxCategories.css';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Age restriction options
const AGE_RESTRICTIONS = [
  { label: 'None', value: 'none' },
  { label: '18+', value: '18+' },
  { label: '21+', value: '21+' }
];

// Licensing requirements
const LICENSING_REQUIREMENTS = [
  { label: 'Tobacco Retailer License', value: 'tobacco_retailer_license' },
  { label: 'FFL Required', value: 'ffl_required' },
  { label: 'State License Needed', value: 'state_license_needed' },
  { label: 'Federal License Required', value: 'federal_license_required' }
];

// Regulatory compliance flags
const REGULATORY_FLAGS = [
  { label: 'FDA Regulated', value: 'FDA_regulated' },
  { label: 'ATF Controlled', value: 'ATF_controlled' },
  { label: 'DEA Controlled', value: 'DEA_controlled' },
  { label: 'ATF Tracked', value: 'ATF_tracked' }
];

// Mock tax categories data - REMOVED - Now using real Firestore data
/* const mockTaxCategories = [
  {
    id: 'TOBACCO_ROOT',
    name: 'Tobacco Products',
    pathString: 'Tobacco Products',
    parentCategoryId: null,
    description: 'All tobacco-related products and accessories',
    keywords: ['tobacco', 'cigarettes', 'cigars', 'smoking'],
    ageRestriction: '21+',
    licensingRequirements: ['tobacco_retailer_license'],
    regulatoryFlags: ['FDA_regulated', 'ATF_tracked'],
    isActive: true,
    level: 0,
    sortOrder: 1,
    children: [
      {
        id: 'TOBACCO_CIGARETTES',
        name: 'Cigarettes',
        pathString: 'Tobacco Products > Cigarettes',
        parentCategoryId: 'TOBACCO_ROOT',
        description: 'Traditional tobacco cigarettes and similar products',
        keywords: ['cigarette', 'tobacco', 'nicotine'],
        ageRestriction: '21+',
        licensingRequirements: ['tobacco_retailer_license'],
        regulatoryFlags: ['FDA_regulated', 'ATF_tracked'],
        isActive: true,
        level: 1,
        sortOrder: 1,
        children: [
          {
            id: 'TOBACCO_CIGARETTES_MENTHOL',
            name: 'Menthol',
            pathString: 'Tobacco Products > Cigarettes > Menthol',
            parentCategoryId: 'TOBACCO_CIGARETTES',
            description: 'Menthol-flavored cigarettes',
            keywords: ['menthol', 'mint', 'cool'],
            ageRestriction: '21+',
            licensingRequirements: ['tobacco_retailer_license'],
            regulatoryFlags: ['FDA_regulated', 'ATF_tracked'],
            isActive: true,
            level: 2,
            sortOrder: 1,
            children: []
          }
        ]
      }
    ]
  },
  {
    id: 'ALCOHOL_ROOT',
    name: 'Alcohol',
    pathString: 'Alcohol',
    parentCategoryId: null,
    description: 'Alcoholic beverages and related products',
    keywords: ['alcohol', 'beer', 'wine', 'spirits'],
    ageRestriction: '21+',
    licensingRequirements: ['state_license_needed'],
    regulatoryFlags: ['ATF_controlled'],
    isActive: true,
    level: 0,
    sortOrder: 2,
    children: [
      {
        id: 'ALCOHOL_BEER',
        name: 'Beer',
        pathString: 'Alcohol > Beer',
        parentCategoryId: 'ALCOHOL_ROOT',
        description: 'Beer and malt beverages',
        keywords: ['beer', 'ale', 'lager', 'malt'],
        ageRestriction: '21+',
        licensingRequirements: ['state_license_needed'],
        regulatoryFlags: ['ATF_controlled'],
        isActive: true,
        level: 1,
        sortOrder: 1,
        children: [
          {
            id: 'ALCOHOL_BEER_CRAFT',
            name: 'Craft Beer',
            pathString: 'Alcohol > Beer > Craft Beer',
            parentCategoryId: 'ALCOHOL_BEER',
            description: 'Artisanal and craft brewery products',
            keywords: ['craft', 'artisanal', 'microbrewery'],
            ageRestriction: '21+',
            licensingRequirements: ['state_license_needed'],
            regulatoryFlags: ['ATF_controlled'],
            isActive: true,
            level: 2,
            sortOrder: 1,
            children: [
              {
                id: 'ALCOHOL_BEER_CRAFT_IPA',
                name: 'IPA',
                pathString: 'Alcohol > Beer > Craft Beer > IPA',
                parentCategoryId: 'ALCOHOL_BEER_CRAFT',
                description: 'India Pale Ale varieties',
                keywords: ['ipa', 'hops', 'bitter', 'pale ale'],
                ageRestriction: '21+',
                licensingRequirements: ['state_license_needed'],
                regulatoryFlags: ['ATF_controlled'],
                isActive: true,
                level: 3,
                sortOrder: 1,
                children: []
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'FIREARMS_ROOT',
    name: 'Firearms',
    pathString: 'Firearms',
    parentCategoryId: null,
    description: 'Firearms, ammunition, and related accessories',
    keywords: ['firearm', 'gun', 'weapon', 'ammunition'],
    ageRestriction: '21+',
    licensingRequirements: ['ffl_required', 'federal_license_required'],
    regulatoryFlags: ['ATF_controlled', 'ATF_tracked'],
    isActive: true,
    level: 0,
    sortOrder: 3,
    children: [
      {
        id: 'FIREARMS_HANDGUNS',
        name: 'Handguns',
        pathString: 'Firearms > Handguns',
        parentCategoryId: 'FIREARMS_ROOT',
        description: 'Pistols, revolvers, and other handguns',
        keywords: ['handgun', 'pistol', 'revolver'],
        ageRestriction: '21+',
        licensingRequirements: ['ffl_required', 'federal_license_required'],
        regulatoryFlags: ['ATF_controlled', 'ATF_tracked'],
        isActive: true,
        level: 1,
        sortOrder: 1,
        children: [
          {
            id: 'FIREARMS_HANDGUNS_REVOLVERS',
            name: 'Revolvers',
            pathString: 'Firearms > Handguns > Revolvers',
            parentCategoryId: 'FIREARMS_HANDGUNS',
            description: 'Revolving cylinder handguns',
            keywords: ['revolver', 'cylinder', 'wheel gun'],
            ageRestriction: '21+',
            licensingRequirements: ['ffl_required', 'federal_license_required'],
            regulatoryFlags: ['ATF_controlled', 'ATF_tracked'],
            isActive: true,
            level: 2,
            sortOrder: 1,
            children: []
          }
        ]
      }
    ]
  },
  {
    id: 'VAPING_ROOT',
    name: 'Vaping Products',
    pathString: 'Vaping Products',
    parentCategoryId: null,
    description: 'Electronic cigarettes, vape devices, and e-liquids',
    keywords: ['vape', 'electronic cigarette', 'e-liquid', 'vaping'],
    ageRestriction: '21+',
    licensingRequirements: ['tobacco_retailer_license'],
    regulatoryFlags: ['FDA_regulated'],
    isActive: true,
    level: 0,
    sortOrder: 4,
    children: [
      {
        id: 'VAPING_ELIQUIDS',
        name: 'E-Liquids',
        pathString: 'Vaping Products > E-Liquids',
        parentCategoryId: 'VAPING_ROOT',
        description: 'Vaping liquids and juices',
        keywords: ['e-liquid', 'vape juice', 'nicotine liquid'],
        ageRestriction: '21+',
        licensingRequirements: ['tobacco_retailer_license'],
        regulatoryFlags: ['FDA_regulated'],
        isActive: true,
        level: 1,
        sortOrder: 1,
        children: [
          {
            id: 'VAPING_ELIQUIDS_NICOTINE_SALT',
            name: 'Nicotine Salt',
            pathString: 'Vaping Products > E-Liquids > Nicotine Salt',
            parentCategoryId: 'VAPING_ELIQUIDS',
            description: 'Nicotine salt-based e-liquids',
            keywords: ['nicotine salt', 'nic salt', 'smooth nicotine'],
            ageRestriction: '21+',
            licensingRequirements: ['tobacco_retailer_license'],
            regulatoryFlags: ['FDA_regulated'],
            isActive: true,
            level: 2,
            sortOrder: 1,
            children: []
          }
        ]
      }
    ]
  }
]; */

const TaxCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [expandedKeys, setExpandedKeys] = useState(['TOBACCO_ROOT', 'ALCOHOL_ROOT']);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  useEffect(() => {
    let unsubscribe;
    
    const setupRealtimeSubscription = () => {
      setLoading(true);
      try {
        // Set up real-time subscription to Firestore
        unsubscribe = taxCategoriesService.subscribeToCategories((categoriesData) => {
          console.log('📡 Real-time update: Received', categoriesData.length, 'categories');
          
          // Build hierarchy from flat data
          const hierarchicalData = taxCategoriesService.buildHierarchy(categoriesData);
          setCategories(hierarchicalData);
          setLoading(false);
          
          // Set initial expanded keys for hierarchy
          const rootKeys = hierarchicalData.map(cat => cat.id);
          setExpandedKeys(prev => [...new Set([...prev, ...rootKeys])]);
        });
      } catch (error) {
        console.error('❌ Error setting up categories subscription:', error);
        message.error(`Failed to load tax categories: ${error.message}`);
        setLoading(false);
      }
    };
    
    setupRealtimeSubscription();
    
    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        console.log('🧹 Cleaning up categories subscription');
        unsubscribe();
      }
    };
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      console.log('📥 Fetching categories from Firestore...');
      const categoriesData = await taxCategoriesService.getAll();
      
      // Build hierarchy from flat data
      const hierarchicalData = taxCategoriesService.buildHierarchy(categoriesData);
      setCategories(hierarchicalData);
      setLoading(false);
      
      console.log('✅ Successfully loaded', categoriesData.length, 'categories');
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      message.error(`Failed to fetch tax categories: ${error.message}`);
      setLoading(false);
    }
  };

  const generatePathString = (parentPath, name) => {
    return parentPath ? `${parentPath} > ${name}` : name;
  };

  // Real-time validation function
  const performValidation = (formValues) => {
    const errors = [];
    const warnings = [];

    // Validate required fields
    if (!formValues.name || formValues.name.trim().length === 0) {
      errors.push('Category name is required');
    }

    if (!formValues.description || formValues.description.trim().length === 0) {
      warnings.push('Description is recommended for better categorization');
    }

    // Validate name uniqueness (simplified check)
    const existingNames = flattenCategories(categories).map(cat => cat.name.toLowerCase());
    if (formValues.name && existingNames.includes(formValues.name.toLowerCase()) && !isEditing) {
      errors.push('Category name already exists');
    }

    // Validate keywords
    if (formValues.keywords && formValues.keywords.length === 0) {
      warnings.push('Adding keywords helps with AI categorization accuracy');
    }

    // Validate regulatory flags
    if (formValues.ageRestriction !== 'none' && (!formValues.licensingRequirements || formValues.licensingRequirements.length === 0)) {
      warnings.push('Age-restricted products typically require licensing');
    }

    setValidationErrors(errors);
    setValidationWarnings(warnings);

    return { isValid: errors.length === 0, errors, warnings };
  };

  // Debounced validation for form changes
  const handleFormChange = () => {
    try {
      const values = form.getFieldsValue();
      performValidation(values);
    } catch (error) {
      // Form validation failed - this is normal during typing
      setValidationErrors([]);
      setValidationWarnings([]);
    }
  };

  const handleSaveCategory = async (values) => {
    setSaving(true);
    try {
      console.log('💾 Saving category...', { isEditing, values });
      
      // Perform validation
      const validation = performValidation(values);
      
      if (!validation.isValid) {
        message.error('Please fix validation errors before saving');
        setSaving(false);
        return;
      }

      const parentCategory = values.parentCategoryId ? 
        findCategoryById(taxCategoriesService.flattenHierarchy(categories), values.parentCategoryId) : null;
      
      const pathString = generatePathString(
        parentCategory?.pathString, 
        values.name
      );

      const categoryData = {
        name: values.name,
        description: values.description,
        parentCategoryId: values.parentCategoryId || null,
        pathString,
        level: parentCategory ? parentCategory.level + 1 : 0,
        sortOrder: values.sortOrder || 1,
        keywords: values.keywords || [],
        licensingRequirements: values.licensingRequirements || [],
        regulatoryFlags: values.regulatoryFlags || [],
        ageRestriction: values.ageRestriction || 'none',
        isActive: values.isActive !== false
      };

      // Save to Firestore using real service
      if (isEditing) {
        console.log('📝 Updating category:', selectedCategory.id);
        await taxCategoriesService.updateCategory(selectedCategory.id, categoryData);
        message.success('Category updated successfully!');
      } else {
        console.log('➕ Creating new category');
        const result = await taxCategoriesService.createCategory({
          ...categoryData,
          id: generateCategoryId(values.name)
        });
        console.log('✅ Category created:', result.id);
        message.success('Category created successfully!');
      }

      // Reset form and close modal
      setEditModalVisible(false);
      form.resetFields();
      setSelectedCategory(null);
      setIsEditing(false);
      setSaving(false);
      
      // Real-time subscription will automatically update the UI

    } catch (error) {
      console.error('❌ Error saving category:', error);
      message.error(`Failed to ${isEditing ? 'update' : 'create'} category: ${error.message}`);
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      console.log('🗑️ Archiving category:', categoryId);
      await taxCategoriesService.archiveCategory(categoryId);
      message.success('Category archived successfully!');
      
      // Real-time subscription will automatically update the UI
    } catch (error) {
      console.error('❌ Error archiving category:', error);
      message.error(`Failed to archive category: ${error.message}`);
    }
  };

  const handleRestoreCategory = async (categoryId) => {
    try {
      console.log('🔄 Restoring category:', categoryId);
      await taxCategoriesService.restoreCategory(categoryId);
      message.success('Category restored successfully!');
      
      // Real-time subscription will automatically update the UI
    } catch (error) {
      console.error('❌ Error restoring category:', error);
      message.error(`Failed to restore category: ${error.message}`);
    }
  };

  const handlePermanentDelete = async (categoryId) => {
    try {
      console.log('💥 Permanently deleting category:', categoryId);
      await taxCategoriesService.deleteCategory(categoryId);
      message.success('Category permanently deleted!');
      
      // Real-time subscription will automatically update the UI
    } catch (error) {
      console.error('❌ Error deleting category:', error);
      message.error(`Failed to delete category: ${error.message}`);
    }
  };

  const generateCategoryId = (name) => {
    return name.toUpperCase().replace(/[^A-Z0-9]/g, '_');
  };

  const findCategoryById = (categories, id) => {
    for (const category of categories) {
      if (category.id === id) return category;
      if (category.children) {
        const found = findCategoryById(category.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const flattenCategories = (categories) => {
    let result = [];
    for (const category of categories) {
      result.push(category);
      if (category.children) {
        result = result.concat(flattenCategories(category.children));
      }
    }
    return result;
  };

  const convertToTreeData = (categories) => {
    return categories.map(category => ({
      title: (
        <div className="tree-node">
          <span className="category-name">{category.name}</span>
          <div className="category-actions">
            <Space size="small">
              {category.ageRestriction !== 'none' && (
                <Tag color="orange" size="small">{category.ageRestriction}</Tag>
              )}
              {category.regulatoryFlags?.map(flag => (
                <Tag key={flag} color="blue" size="small">
                  {flag.replace('_', ' ')}
                </Tag>
              ))}
              <Tooltip title="Edit Category">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditCategory(category);
                  }}
                />
              </Tooltip>
              <Tooltip title="Archive Category">
                <Popconfirm
                  title="Are you sure you want to archive this category?"
                  onConfirm={(e) => {
                    e.stopPropagation();
                    handleDeleteCategory(category.id);
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </Tooltip>
            </Space>
          </div>
        </div>
      ),
      key: category.id,
      icon: category.children && category.children.length > 0 ? 
        <FolderOutlined /> : <FileOutlined />,
      children: category.children && category.children.length > 0 ? 
        convertToTreeData(category.children) : undefined
    }));
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setIsEditing(true);
    setEditModalVisible(true);
    
    form.setFieldsValue({
      name: category.name,
      description: category.description,
      parentCategoryId: category.parentCategoryId,
      keywords: category.keywords,
      ageRestriction: category.ageRestriction || 'none',
      licensingRequirements: category.licensingRequirements || [],
      regulatoryFlags: category.regulatoryFlags || [],
      isActive: category.isActive
    });
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setIsEditing(false);
    setEditModalVisible(true);
    form.resetFields();
    form.setFieldsValue({
      ageRestriction: 'none',
      isActive: true
    });
  };

  const onTreeSelect = (selectedKeys, info) => {
    if (selectedKeys.length > 0) {
      const categoryId = selectedKeys[0];
      const category = findCategoryById(categories, categoryId);
      setSelectedCategory(category);
    }
  };

  const filteredTreeData = searchValue ? 
    filterTreeData(convertToTreeData(categories), searchValue) : 
    convertToTreeData(categories);

  function filterTreeData(data, searchValue) {
    const loop = (data) =>
      data
        .map((item) => {
          const strTitle = item.title.props.children[0].props.children;
          const index = strTitle.toLowerCase().indexOf(searchValue.toLowerCase());
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(index + searchValue.length);
          const title =
            index > -1 ? (
              <span>
                {beforeStr}
                <span style={{ color: '#f50' }}>{searchValue}</span>
                {afterStr}
              </span>
            ) : (
              <span>{strTitle}</span>
            );
          if (index > -1) {
            return {
              ...item,
              title,
            };
          }
          const children = item.children ? loop(item.children) : [];
          if (children.length > 0) {
            return {
              ...item,
              children,
            };
          }
          return null;
        })
        .filter((item) => item);
    return loop(data);
  }

  const getAllParentCategories = () => {
    const allCategories = flattenCategories(categories);
    return allCategories.filter(cat => cat.level < 3); // Allow up to 3 levels deep
  };

  return (
    <div className="tax-categories">
      <Layout>
        <Sider width={400} theme="light" className="categories-sider">
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>Tax Categories Hierarchy</Title>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddCategory}
                  size="small"
                >
                  Add Category
                </Button>
              </div>
            }
            size="small"
            bodyStyle={{ padding: '12px' }}
          >
            <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
              <Input
                placeholder="Search categories..."
                prefix={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                allowClear
              />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={fetchCategories}
                  loading={loading}
                >
                  Refresh
                </Button>
                <Text type="secondary">
                  {flattenCategories(categories).length} categories
                </Text>
              </div>
            </Space>

            <Tree
              showIcon
              showLine
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              treeData={filteredTreeData}
              onSelect={onTreeSelect}
              height={600}
              style={{ fontSize: '14px' }}
            />
          </Card>
        </Sider>

        <Layout>
          <Content style={{ padding: '24px' }}>
            {selectedCategory ? (
              <Card 
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>
                      Category Details: {selectedCategory.name}
                    </Title>
                    <Space>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => handleEditCategory(selectedCategory)}
                      >
                        Edit Category
                      </Button>
                    </Space>
                  </div>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Text strong>Path: </Text>
                    <Text code>{selectedCategory.pathString}</Text>
                  </div>
                  
                  <div>
                    <Text strong>Description: </Text>
                    <Text>{selectedCategory.description}</Text>
                  </div>

                  <div>
                    <Text strong>Keywords: </Text>
                    <Space wrap>
                      {selectedCategory.keywords?.map(keyword => (
                        <Tag key={keyword} color="blue">{keyword}</Tag>
                      ))}
                    </Space>
                  </div>

                  <div>
                    <Text strong>Age Restriction: </Text>
                    <Tag color={selectedCategory.ageRestriction !== 'none' ? 'orange' : 'default'}>
                      {selectedCategory.ageRestriction}
                    </Tag>
                  </div>

                  <div>
                    <Text strong>Licensing Requirements: </Text>
                    <Space wrap>
                      {selectedCategory.licensingRequirements?.map(req => (
                        <Tag key={req} color="purple">{req.replace(/_/g, ' ')}</Tag>
                      ))}
                    </Space>
                  </div>

                  <div>
                    <Text strong>Regulatory Flags: </Text>
                    <Space wrap>
                      {selectedCategory.regulatoryFlags?.map(flag => (
                        <Tag key={flag} color="red">{flag.replace(/_/g, ' ')}</Tag>
                      ))}
                    </Space>
                  </div>

                  <div>
                    <Text strong>Status: </Text>
                    <Tag color={selectedCategory.isActive ? 'green' : 'red'}>
                      {selectedCategory.isActive ? 'Active' : 'Archived'}
                    </Tag>
                  </div>

                  <div>
                    <Text strong>Level: </Text>
                    <Text>{selectedCategory.level}</Text>
                  </div>
                </Space>
              </Card>
            ) : (
              <Card>
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <FolderOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />
                  <Title level={3} type="secondary">Select a category to view details</Title>
                  <Text type="secondary">
                    Choose a category from the tree on the left to see its taxonomy information
                  </Text>
                </div>
              </Card>
            )}
          </Content>
        </Layout>
      </Layout>

      {/* Add/Edit Category Modal */}
      <Modal
        title={isEditing ? 'Edit Tax Category' : 'Add New Tax Category'}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
          setValidationErrors([]);
          setValidationWarnings([]);
        }}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={isEditing ? 'Update Category' : 'Create Category'}
        width={800}
        destroyOnClose
      >
        {/* Validation Alerts */}
        {validationErrors.length > 0 && (
          <Alert
            message="Validation Errors"
            description={
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {validationWarnings.length > 0 && (
          <Alert
            message="Validation Warnings"
            description={
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationWarnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveCategory}
          onFieldsChange={handleFormChange}
        >
          <Form.Item
            label="Category Name"
            name="name"
            rules={[
              { required: true, message: 'Please enter category name' },
              { min: 2, message: 'Name must be at least 2 characters' }
            ]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            label="Parent Category"
            name="parentCategoryId"
            help="Leave empty to create a root category"
          >
            <Select placeholder="Select parent category" allowClear>
              {getAllParentCategories().map(category => (
                <Option key={category.id} value={category.id}>
                  {category.pathString}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: 'Please enter description' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Describe this category and what products it includes"
            />
          </Form.Item>

          <Form.Item
            label="Keywords"
            name="keywords"
            help="Keywords help with AI-powered product categorization"
          >
            <Select
              mode="tags"
              placeholder="Add keywords for AI categorization (press Enter to add)"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Divider orientation="left">Regulatory Information</Divider>

          <Form.Item
            label="Age Restriction"
            name="ageRestriction"
          >
            <Select placeholder="Select age restriction">
              {AGE_RESTRICTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Licensing Requirements"
            name="licensingRequirements"
          >
            <Checkbox.Group options={LICENSING_REQUIREMENTS} />
          </Form.Item>

          <Form.Item
            label="Regulatory Compliance Flags"
            name="regulatoryFlags"
          >
            <Checkbox.Group options={REGULATORY_FLAGS} />
          </Form.Item>

          <Form.Item
            label="Status"
            name="isActive"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Archived" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TaxCategories;